import React from "react";
import { Link } from "react-router-dom";
import {
  Disc3, Activity, AlignVerticalSpaceAround, Bone, MoveVertical,
  ScanLine, PersonStanding, Move3D, Footprints, ArrowUpRight,
} from "lucide-react";
import { RevealStagger, StaggerItem, Reveal } from "@/components/Reveal";
import { CONDITIONS } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const ICONS = {
  Disc3, Activity, AlignVerticalSpaceAround, Bone, MoveVertical,
  ScanLine, PersonStanding, Move3D, Footprints,
};

const SLUGS = {
  "Slipped Disc": "/slipped-disc-treatment",
  "Sciatica": "/sciatica-treatment",
  "Lumbar Canal Stenosis": "/lumbar-canal-stenosis-treatment",
  "Cervical Disc Disease": "/cervical-disc-disease-treatment",
  "Back Pain": "/back-pain-treatment-hyderabad",
  "Spondylolisthesis": "/spondylolisthesis-treatment",
  "Spinal Tumours": "/spinal-tumour-treatment",
  "Neck Pain": "/neck-pain-treatment",
  "Foot Drop": "/foot-drop-treatment",
};

const ANCHORS = {
  "Slipped Disc": "Slipped disc treatment",
  "Sciatica": "Sciatica treatment",
  "Lumbar Canal Stenosis": "Lumbar canal stenosis treatment",
  "Cervical Disc Disease": "Cervical disc disease treatment",
  "Back Pain": "Back pain treatment",
  "Spondylolisthesis": "Spondylolisthesis treatment",
  "Spinal Tumours": "Spinal tumour treatment",
  "Neck Pain": "Neck pain treatment",
  "Foot Drop": "Foot drop treatment",
};

const Conditions = () => {
  const { openBooking } = useBooking();
  return (
    <section id="conditions" data-testid="conditions-grid" className="relative bg-slate-50 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">02 — Common Conditions</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            Spine conditions we evaluate and treat
          </h2>
          <p className="mt-5 text-lg text-slate-500">
            From nerve compression to chronic pain — each condition is assessed individually to
            recommend the most appropriate path forward.
          </p>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CONDITIONS.map((c) => {
            const Icon = ICONS[c.icon] || Activity;
            const slug = SLUGS[c.title];
            const commonCls = "group text-left w-full h-full glass-card rounded-2xl p-7 hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300 relative overflow-hidden block";
            const inner = (
              <>
                <div className="w-12 h-12 rounded-xl bg-brand-light border border-brand/15 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-brand group-hover:border-brand transition-all">
                  <Icon className="w-6 h-6 text-brand group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-extrabold text-[#0b2c4d] text-xl tracking-tight">{c.title}</h3>
                <p className="mt-2 text-slate-500 text-sm leading-relaxed">{c.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                  {ANCHORS[c.title] || "Learn More"}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </>
            );
            const testId = `condition-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`;
            return (
              <StaggerItem key={c.title}>
                {slug ? (
                  <Link to={slug} data-testid={testId} className={commonCls}>{inner}</Link>
                ) : (
                  <button
                    onClick={() => openBooking("appointment")}
                    data-testid={testId}
                    className={commonCls}
                  >
                    {inner}
                  </button>
                )}
              </StaggerItem>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
};

export default Conditions;
